import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import type { BidType } from '@/features/bidding/constants'
import { GET_BID_UPDATES_API_ID, getBidUpdates } from '@/features/bidding/endpoints/bids'
import { useErrorHandler } from '@/features/bidding/hooks/useErrorHandler'
import type { BidUpdate } from '@/features/bidding/types/bidUpdate'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import type { UseQueryOptions } from '@/types/queries'

type BidUpdatesQueryParams = {
  activationGroupUuid?: string
  deliveryDay?: MarketDate
  bidType: BidType
}

export type UseBidUpdatesQueryResult = Omit<UseQueryResult, 'data'> & {
  bidUpdates?: BidUpdate[]
}

export function useBidUpdatesQuery(
  params: BidUpdatesQueryParams,
  options?: UseQueryOptions<BidUpdate[]>,
): UseBidUpdatesQueryResult {
  const { data, ...queryResult } = useQuery({
    ...options,
    queryKey: [GET_BID_UPDATES_API_ID, params.activationGroupUuid, params.deliveryDay?.toISODate(), params.bidType],
    queryFn: () => getBidUpdates(params.activationGroupUuid!, params.deliveryDay!, params.bidType),
  })

  useErrorHandler(queryResult.isError)

  return { bidUpdates: data, ...queryResult }
}

export function useInvalidateBidUpdatesQuery() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: [GET_BID_UPDATES_API_ID] })
}
